import * as React from 'react';

import { cn } from '@/lib/utils';

/*
 * shadcn table primitives, restyled to the prototype's mono/bevel data look:
 * IBM Plex Mono cells, inset panel surface, hard header divider, zero radius.
 */

function Table({ className, 'aria-label': ariaLabel, ...props }: React.ComponentProps<'table'>) {
  return (
    // tabIndex + region: keyboard-only users must be able to focus the
    // container to scroll it horizontally when the table overflows
    // (WCAG 2.1.1). The region takes the table's aria-label as its name.
    <div
      data-slot="table-container"
      className="bevel-in bg-panel-in relative w-full overflow-x-auto"
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      <table
        data-slot="table"
        aria-label={ariaLabel}
        className={cn('w-full caption-bottom font-mono text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('bg-title [&_tr]:border-b [&_tr]:border-bev-d', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('border-line hover:bg-panel/60 border-b transition-colors', className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-titletext font-display h-9 px-3 text-left align-middle text-[10px] uppercase tracking-wide',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('text-text px-3 py-2 align-middle', className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
